/**
 * Person and company pages (SPEC §5.9, §7.3). A panel with what connects them (a company's via
 * chain in both directions, its people and projects; a person's company and when you last met),
 * then the timelines as editable rows: open tasks, meetings by day. Mentions follow in the node
 * view as linked references (without what is already listed here).
 */
import { ArrowRight } from 'lucide-react'
import type { ReactNode } from 'react'
import {
  companyOf,
  isCompany,
  isPerson,
  isOpenStatus,
  keyForId,
  keyForTitle,
  ME_FIELD,
  mergedTarget,
  nodeDay,
  peopleAt,
  plainText,
  servedThrough,
  withStatus,
  TAG,
  viaChain,
  type Graph,
  type Node,
  type NodeId,
} from '@solar/core'
import type { ClientStore, ViewRow } from '../client/store.ts'
import { dayTitle, todayKey } from './dates.ts'
import { useStore, useUi } from './hooks.ts'
import { referenceRows } from './refrows.ts'
import { navigate } from './router.ts'

export const isEntity = (g: Graph, n: Node): boolean => isPerson(g, n) || isCompany(g, n)

const ids = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : typeof v === 'string' ? [v] : [])

/** Nodes that refer to the node by title or id (text refs, field values). */
function referrers(store: ClientStore, n: Node): Set<NodeId> {
  const out = new Set(store.refs.referrers(keyForId(n.id)))
  if (n.title) for (const x of store.refs.referrers(keyForTitle(n.title))) out.add(x)
  return out
}

/** Meetings connected to a person (attendee, mentioned) or a company (company field, mentioned, an attendee works there). */
function meetingsOf(store: ClientStore, n: Node): Node[] {
  const g = store.g
  const meeting = g.get(mergedTarget(g, TAG.meeting))
  if (!meeting) return []
  const near = referrers(store, n)
  const people = isCompany(g, n) ? new Set(peopleAt(g, store.refs, n.id).map((p) => p.id)) : null
  return store.refs
    .taggedWith(meeting)
    .map((id) => g.must(id))
    .filter((m) => {
      if (near.has(m.id)) return true
      if (people && ids(m.props['meeting.attendees']).some((a) => people.has(mergedTarget(g, a)))) return true
      return false
    })
}

function meetingDay(g: Graph, m: Node): string {
  const d = m.props['meeting.date']
  return typeof d === 'string' ? d.slice(0, 10) : nodeDay(g, m)
}

/**
 * Open tasks of a person (assigned to them or naming them) or a company (naming it, or assigned
 * to or naming one of its people).
 */
function openTasksOf(store: ClientStore, n: Node): Node[] {
  const g = store.g
  const team = isCompany(g, n) ? peopleAt(g, store.refs, n.id) : [n]
  const people = new Set(team.map((p) => p.id))
  const near = referrers(store, n)
  for (const p of team) for (const x of referrers(store, p)) near.add(x)
  return store.tasks.all().filter((t) => {
    if (!isOpenStatus(store.tasks.statusOf(t))) return false
    const who = ids(t.props['task.assignee']).map((a) => mergedTarget(g, a))
    return who.some((a) => people.has(a)) || near.has(t.id)
  })
}

/** The rows of a person's or company's timelines; `shown` are left out of the linked references. */
export function entitySections(store: ClientStore, n: Node): { rows: ViewRow[]; shown: Set<NodeId> } {
  const rows: ViewRow[] = []
  const shown = new Set<NodeId>()
  // People and served companies are in the panel already; their field refs are not "mentions".
  if (isCompany(store.g, n)) {
    for (const p of peopleAt(store.g, store.refs, n.id)) shown.add(p.id)
    for (const c of servedThrough(store.g, store.refs, n.id)) shown.add(c.id)
  }
  const tasks = openTasksOf(store, n)
  if (tasks.length > 0) {
    const r = referenceRows(store, n, tasks.map((t) => t.id), 'ent-t:')
    rows.push({ type: 'label', key: 'ent-tasks', level: 'section', text: `${r.count} open task${r.count === 1 ? '' : 's'}` })
    rows.push(...r.rows)
    tasks.forEach((t) => shown.add(t.id))
  }
  const meetings = meetingsOf(store, n)
  if (meetings.length > 0) {
    const r = referenceRows(store, n, meetings.map((m) => m.id), 'ent-m:')
    rows.push({ type: 'label', key: 'ent-meetings', level: 'section', text: `${r.count} meeting${r.count === 1 ? '' : 's'}` })
    rows.push(...r.rows)
    meetings.forEach((m) => shown.add(m.id))
  }
  return { rows, shown }
}

function Link({ n }: { n: Node }) {
  return (
    <a className="entity-link" href={`/n/${n.id}`} onClick={(e) => (e.preventDefault(), navigate(`/n/${n.id}`))}>
      {n.title}
    </a>
  )
}

function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="field">
      <span className="fname">{label}</span>
      <span className="fval entity-val">{children}</span>
    </div>
  )
}

/** What connects a person or company, under its fields. */
export function EntityPanel({ node }: { node: Node }) {
  const store = useStore()
  useUi()
  const g = store.g
  if (isPerson(g, node)) {
    const today = todayKey()
    const met = meetingsOf(store, node)
      .map((m) => meetingDay(g, m))
      .filter((d) => d <= today)
      .sort()
      .at(-1)
    const company = companyOf(g, node)
    const me = node.props[ME_FIELD] === true
    return (
      <div className="fields entity">
        {company && viaChain(g, company.id).length > 0 && (
          <Line label="Via">
            {viaChain(g, company.id).map((c, i) => (
              <span key={c.id}>
                {i > 0 && <ArrowRight size={12} aria-hidden />} <Link n={c} />
              </span>
            ))}
          </Line>
        )}
        <Line label="Last met">{met ? dayTitle(met).title : <span className="empty">No meeting yet</span>}</Line>
        <Line label="This is me">
          <input type="checkbox" aria-label="This is me" checked={me} onChange={(e) => store.setMe(e.target.checked ? node.id : null)} />
        </Line>
      </div>
    )
  }
  const chain = viaChain(g, node.id)
  const serves = servedThrough(g, store.refs, node.id)
  const people = peopleAt(g, store.refs, node.id)
  const project = g.get(mergedTarget(g, TAG.project))
  const projects = project
    ? store.refs
        .taggedWith(project)
        .map((id) => g.must(id))
        .filter((p) => mergedTarget(g, String(p.props['project.company'] ?? '')) === node.id)
    : []
  if (chain.length + serves.length + people.length + projects.length === 0) return null
  return (
    <div className="fields entity">
      {chain.length > 0 && (
        <Line label="Served through">
          {chain.map((c, i) => (
            <span key={c.id}>
              {i > 0 && <ArrowRight size={12} aria-hidden />} <Link n={c} />
            </span>
          ))}
        </Line>
      )}
      {serves.length > 0 && (
        <Line label="Serves">
          {serves.map((c, i) => (
            <span key={c.id}>
              {i > 0 && ', '}
              <Link n={c} />
            </span>
          ))}
        </Line>
      )}
      {people.length > 0 && (
        <Line label="People">
          {people.map((p, i) => (
            <span key={p.id}>
              {i > 0 && ', '}
              <Link n={p} />
              {typeof p.props['person.role'] === 'string' && <span className="muted"> ({String(p.props['person.role'])})</span>}
            </span>
          ))}
        </Line>
      )}
      {projects.length > 0 && (
        <Line label="Projects">
          {projects.map((p, i) => (
            <span key={p.id}>
              {i > 0 && ', '}
              {p.title ? (
                <Link n={p} />
              ) : (
                <a className="entity-link" href={`/n/${p.id}`} onClick={(e) => (e.preventDefault(), navigate(`/n/${p.id}`))}>
                  {plainText(withStatus(p.content, null)).replace(/#\S+/g, '').trim()}
                </a>
              )}
            </span>
          ))}
        </Line>
      )}
    </div>
  )
}
